/**
 * Cookie Consent component for GDPR compliance
 * Works with Google Analytics consent mode
 */

import React, { useState, useEffect } from 'react';
import { getGoogleAnalytics } from '../../utils/analytics';
import { useLanguage } from '../../contexts/LanguageContext';

interface CookieConsentProps {
  onAccept?: () => void;
  onReject?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { language } = useLanguage();

  // Text content based on language
  const content = language === 'en' ? {
    message: "We use cookies to analyze traffic and improve the site. By continuing to use the site, you agree to our",
    policyLink: "privacy policy",
    reject: "Reject",
    accept: "Accept"
  } : {
    message: "Ми використовуємо файли cookie для аналізу трафіку та покращення роботи сайту. Продовжуючи використання сайту, ви погоджуєтесь з нашою",
    policyLink: "політикою конфіденційності",
    reject: "Відхилити",
    accept: "Прийняти"
  };

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    } else if (consent === 'accepted') {
      // Apply previously accepted consent
      const ga = getGoogleAnalytics();
      if (ga) {
        ga.consentUpdate('granted');
      }
    }

    // Determine if mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    
    // Update Google Analytics consent
    const ga = getGoogleAnalytics();
    if (ga) {
      ga.consentUpdate('granted');
    }
    
    setIsVisible(false);
    onAccept?.();
  };

  const handleReject = () => {
    localStorage.setItem('cookie-consent', 'rejected');
    
    // Update Google Analytics consent
    const ga = getGoogleAnalytics();
    if (ga) {
      ga.consentUpdate('denied');
    }
    
    setIsVisible(false);
    onReject?.();
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      border: '1px solid #ddd',
      padding: '16px',
      zIndex: 9999,
      boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
    }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', padding: '0 16px' }}>
          <p style={{ margin: 0, flex: 1, fontSize: '14px', color: '#fff', textAlign: 'center' }}>
            {content.message}{' '}
            <a href="/privacy-policy" style={{ color: '#1976d2', textDecoration: 'none' }}>
              {content.policyLink}
            </a>.
          </p>
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            ...(isMobile && { 
              width: '100%', 
              justifyContent: 'center' 
            })
          }}>
            <button
              onClick={handleReject}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                backgroundColor: '#fff',
                color: '#333',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {content.reject}
            </button>
            <button
              onClick={handleAccept}
              style={{
                padding: '8px 16px',
                border: 'none',
                backgroundColor: '#1976d2',
                color: '#fff',
                cursor: 'pointer',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              {content.accept}
            </button>
          </div>
      </div>
    </div>
  );
};

export default CookieConsent;
